import { useEffect, useState, useCallback } from "react";
import axios from "../../../util/axiosConfig";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../global/AuthContext";
import LevelBadge from "../../../global/LevelBadge";
import { formatBoardDate } from "../../../component/board/BoardDetail";
import "../../../assets/css/container/board/boardClasses/NoticeList.css";

type NoticeItem = {
	id: number;
	title: string;
	createdAt: string;
	authorNickname: string;
	authorLevel: number;
	viewCount: number;
};

type ApiResponse = {
	items: NoticeItem[];
	total: number;
};

export default function NoticeList() {
	const [notices, setNotices] = useState<NoticeItem[]>([]);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);

	const pageSize = 10;

	/* ✅ 블록 페이지네이션 */
	const blockSize = 10;
	const [pageBlock, setPageBlock] = useState(0);

	const maxPage = Math.max(1, Math.ceil(total / pageSize));

	const startPage = pageBlock * blockSize + 1;
	const endPage = Math.min(startPage + blockSize - 1, maxPage);

	const pageNumbers: number[] = [];
	for (let i = startPage; i <= endPage; i++) {
		pageNumbers.push(i);
	}

	const { user } = useAuth();
	const navigate = useNavigate();

	const fetchPage = useCallback(async (targetPage: number) => {
		try {
			setLoading(true);

			const res = await axios.get<ApiResponse>("/api/notices", {
				params: {
					page: targetPage,
					pageSize,
				},
			});

			setNotices(res.data.items);
			setTotal(res.data.total);
			setPage(targetPage);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		setPage(1);
		setPageBlock(0);
		fetchPage(1);
	}, [fetchPage]);

	const goPrevBlock = () => {
		if (pageBlock > 0) {
			setPageBlock(pageBlock - 1);
		}
	};

	const goNextBlock = () => {
		if ((pageBlock + 1) * blockSize < maxPage) {
			setPageBlock(pageBlock + 1);
		}
	};

	return (
		<div className="NoticeList">
			<h2 className="NoticeHeader">공지사항</h2>

			{user?.role === "admin" && (
				<button
					onClick={() => navigate("/notices/write")}
					className="btn-write"
				>
					공지 작성하기
				</button>
			)}

			{notices.length === 0 && !loading ? (
				<p className="NoticeEmpty">공지사항이 없습니다.</p>
			) : (
				<div className="notice-scroll">
					<table className="notice-table">
						<colgroup><col className="NoticeNumber" /><col /><col className="NoticeAuthor" /><col className="NoticeDate" /><col className="NoticeViews" /></colgroup>
						<thead><tr><th scope="col">번호</th><th scope="col">제목</th><th scope="col">글쓴이</th><th scope="col">등록일</th><th scope="col">조회</th></tr></thead>
						<tbody>{notices.map((notice) => (
							<tr key={notice.id} onClick={() => navigate(`/notices/${notice.id}`)}>
								<td className="notice-number">{notice.id.toLocaleString()}</td>
								<td className="notice-title" title={notice.title}>{notice.title}</td>
								<td className="notice-author" title={notice.authorNickname}><LevelBadge level={notice.authorLevel} /><span>{notice.authorNickname}</span></td>
								<td className="notice-date"><time dateTime={notice.createdAt}>{formatBoardDate(notice.createdAt)}</time></td>
								<td className="notice-views">{notice.viewCount.toLocaleString()}</td>
							</tr>
						))}</tbody>
					</table>
				</div>
			)}

			{/* ✅ 블록형 페이지네이션 */}
			<nav className="notice-pagination">
				<button onClick={goPrevBlock} disabled={pageBlock === 0}>
					이전
				</button>

				{pageNumbers.map((p) => (
					<button
						key={p}
						className={p === page ? "active-page" : ""}
						onClick={() => fetchPage(p)}
						disabled={loading}
					>
						{p}
					</button>
				))}

				<button
					onClick={goNextBlock}
					disabled={(pageBlock + 1) * blockSize >= maxPage}
				>
					다음
				</button>
			</nav>
		</div>
	);
}
